/**
 * Vehicle Pricing — computation utilities
 *
 * Single source of truth for the financial model.
 * All pricing computation happens client-side.
 * The Postgres database only stores inputs.
 *
 * Financial model — Rate-Based with Year 1 Insurance Settlement:
 *
 * All investments earn the same annual simple interest rate (r).
 *
 * The model prices ONE year of insurance, settled in Year 1 (52 weeks):
 *   insuranceTotal   = I × (1 + r)
 *   insuranceWeekly  = insuranceTotal / 52
 *
 * In Year 1 the driver pays a flat W, split three ways (partner,
 * management, insurance). After Year 1 the insurance slot ends and the
 * driver pays the pool weekly (partner + management) for the rest of the
 * contract. A renewal Primelane advances is not priced here: it is added
 * to the driver's total as its own advance (priced on the Renewal Pricing page).
 *
 * Contract duration is derived (not input):
 *   poolWeekly = W − I(1+r)/52
 *   T = P / (52 × poolWeekly − P r)
 *   N = 52T  (weeks)
 *   D = poolTotal + I(1+r), poolTotal = poolWeekly × N  (driver price, derived)
 *
 * Weekly splits (each calculated independently):
 *   insuranceWeekly    = I(1+r) / 52   (weeks 1 to 52 only)
 *   managementWeekly   = managementFee / N
 *   partnerWeekly      = partnerTotalReturn / N
 *
 * Each weekly amount is derived from its own total / contractWeeks,
 * and partnerWeekly + managementWeekly = poolWeekly.
 */

export interface PricingInputs {
  weeklyRate: number;               // W — e.g. 1,700
  partnerInvestment: number;        // P — e.g. 104,950
  insuranceInvestment: number;      // I — e.g. 8,431.5
  insuranceActualCost: number;      // C — e.g. 764
  annualInterestRate: number;       // r — e.g. 0.48 (48%), same for ALL
  managementFeeRate: number;        // m — e.g. 0.18 (18%)
}

export interface PricingOutputs {
  // Contract (derived)
  contractWeeks: number;            // N = 52T
  contractYears: number;            // T
  driverPrice: number;              // D = poolTotal + I(1+r), poolTotal = poolWeekly × N (derived)
  poolWeekly: number;               // W − I(1+r)/52: the partner + management share of W, paid for all N weeks
  poolTotal: number;                // poolWeekly × N: the partner + management pool paid over the contract

  // Weekly splits (partner + management run all N weeks; insurance weeks 1 to 52)
  partnerWeekly: number;
  managementWeekly: number;
  insuranceWeekly: number;

  // Partner metrics
  partnerGrossProfit: number;       // P × r × T
  partnerNetProfit: number;         // grossProfit × (1 − m)
  partnerTotalReturn: number;       // P + netProfit
  partnerRoiPct: number;            // netProfit / P × 100
  partnerAnnualizedRoi: number;     // roiPct / T
  partnerBreakevenWeeks: number;    // P ÷ partnerWeekly, exact (not rounded up)

  // Insurance metrics (Year 1 only)
  insuranceTotal: number;           // I × (1 + r)
  insuranceInterest: number;        // I × r
  insuranceBreakevenWeeks: number;  // weeks to recover investment
  insuranceRoiPct: number;          // interest / I × 100

  // Primelane profits
  primelaneInsuranceProfit: number;  // insuranceTotal − C
  primelaneManagementProfit: number; // managementFee total

  // Spill info (display only): driver kept at the full W after week 52 with no renewal advance; freed insurance slot splits (1 − m) partner, m management
  spillPerWeek: number;             // insuranceWeekly: the insurance slot freed after week 52 when the driver is kept at the full W
  spillToPartner: number;           // spill × (1 − m)
  spillToManagement: number;        // spill × m

  // Effective rate (for display)
  annualInterestRate: number;       // echo back for display
}


/**
 * Compute all pricing outputs from inputs.
 *
 * Rate-based model:
 *   1. insuranceTotal = I × (1 + r) — settled in 52 weeks
 *   2. T = P / (52 × poolWeekly − P r), poolWeekly = W − I(1+r)/52 — contract years
 *   3. managementWeekly = managementFee / N
 *   4. partnerWeekly = partnerTotalReturn / N
 *   5. D = poolTotal + I(1+r), poolTotal = poolWeekly × N — driver price derived
 */
export function computePricing(inputs: PricingInputs): PricingOutputs {
  const {
    weeklyRate,
    partnerInvestment,
    insuranceInvestment,
    insuranceActualCost,
    annualInterestRate,
    managementFeeRate,
  } = inputs;

  const W = weeklyRate;
  const P = partnerInvestment;
  const I = insuranceInvestment;
  const C = insuranceActualCost;
  const r = annualInterestRate;
  const m = managementFeeRate;

  if (W <= 0 || P <= 0) {
    return getEmptyOutputs(r);
  }

  // Insurance (Year 1 only)
  const insuranceTotal = I * (1 + r);
  const insuranceInterest = I * r;
  const insuranceWeekly = insuranceTotal / 52;
  const insuranceBreakevenWeeks = insuranceWeekly > 0
    ? Math.ceil(I / insuranceWeekly)
    : 0;
  const insuranceRoiPct = I > 0 ? (insuranceInterest / I) * 100 : 0;

  // Contract duration: T = P / (52 × poolWeekly − P r)
  // poolWeekly is the partner + management share of W; 52 × poolWeekly = 52W − I(1+r).
  const poolWeekly = W - insuranceWeekly;
  const denominator = 52 * poolWeekly - P * r;
  if (denominator <= 0) {
    return getEmptyOutputs(r);
  }

  const contractYears = P / denominator;
  const contractWeeks = 52 * contractYears;

  if (contractYears <= 0) {
    return getEmptyOutputs(r);
  }

  // Owner ruling 2026-09-22: insurance is priced for one year. The driver pays the pool
  // weekly for all N weeks plus the Year 1 insurance total; a renewal Primelane advances
  // later is added to the driver's total, not priced here.
  const poolTotal = poolWeekly * contractWeeks;
  const driverPrice = poolTotal + insuranceTotal;

  // Partner metrics
  const partnerGrossProfit = P * r * contractYears;
  const managementFee = partnerGrossProfit * m;
  const partnerNetProfit = partnerGrossProfit - managementFee;
  const partnerTotalReturn = P + partnerNetProfit;
  const partnerRoiPct = (partnerNetProfit / P) * 100;
  const partnerAnnualizedRoi = contractYears > 0
    ? partnerRoiPct / contractYears
    : 0;

  // Weekly splits (each from its own total)
  const managementWeekly = managementFee / contractWeeks;
  const partnerWeekly = partnerTotalReturn / contractWeeks;

  if (partnerWeekly <= 0) {
    return getEmptyOutputs(r);
  }

  // Exact weeks, never rounded up to a whole week: the same quotient the Vehicle Schedule's
  // Capital recovery prints to two decimals, so the page and the paper show the same figure.
  const partnerBreakevenWeeks = partnerWeekly > 0
    ? P / partnerWeekly
    : 0;

  // Primelane profits
  const primelaneInsuranceProfit = insuranceTotal - C;
  const primelaneManagementProfit = managementFee;

  // Spill info (driver kept at the full W after week 52 with no renewal advance; freed insurance slot splits (1 − m) partner, m management)
  const spillPerWeek = insuranceWeekly;
  const spillToPartner = spillPerWeek * (1 - m);
  const spillToManagement = spillPerWeek * m;

  return {
    contractWeeks,
    contractYears,
    driverPrice,
    poolWeekly,
    poolTotal,
    partnerWeekly,
    managementWeekly,
    insuranceWeekly,
    partnerGrossProfit,
    partnerNetProfit,
    partnerTotalReturn,
    partnerRoiPct,
    partnerAnnualizedRoi,
    partnerBreakevenWeeks,
    insuranceTotal,
    insuranceInterest,
    insuranceBreakevenWeeks,
    insuranceRoiPct,
    primelaneInsuranceProfit,
    primelaneManagementProfit,
    spillPerWeek,
    spillToPartner,
    spillToManagement,
    annualInterestRate: r,
  };
}

/**
 * Default pricing inputs.
 */
export function getDefaultPricingInputs(): PricingInputs {
  return {
    weeklyRate: 1700,
    partnerInvestment: 104950,
    insuranceInvestment: 8379,
    insuranceActualCost: 748,
    annualInterestRate: 0.43,
    managementFeeRate: 0.18,
  };
}

/**
 * Map DB key-value rows to PricingInputs.
 */
export function dbRowsToInputs(rows: Array<{ key: string; value: string }>): PricingInputs {
  const defaults = getDefaultPricingInputs();
  const map = new Map(rows.map((r) => [r.key, r.value]));

  return {
    weeklyRate: parseFloat(map.get('weekly_rate') ?? '') || defaults.weeklyRate,
    partnerInvestment: parseFloat(map.get('partner_investment') ?? '') || defaults.partnerInvestment,
    insuranceInvestment: parseFloat(map.get('insurance_investment') ?? '') || defaults.insuranceInvestment,
    insuranceActualCost: parseFloat(map.get('insurance_actual_cost') ?? '') || defaults.insuranceActualCost,
    annualInterestRate: parseFloat(map.get('annual_interest_rate') ?? '') || defaults.annualInterestRate,
    managementFeeRate: parseFloat(map.get('management_fee_rate') ?? '') || defaults.managementFeeRate,
  };
}

/**
 * Convert PricingInputs back to DB rows for upsert.
 */
export function inputsToDbRows(inputs: PricingInputs): Array<{ key: string; value: string; sort_order: number }> {
  return [
    { key: 'weekly_rate', value: String(inputs.weeklyRate), sort_order: 0 },
    { key: 'partner_investment', value: String(inputs.partnerInvestment), sort_order: 1 },
    { key: 'insurance_investment', value: String(inputs.insuranceInvestment), sort_order: 2 },
    { key: 'insurance_actual_cost', value: String(inputs.insuranceActualCost), sort_order: 3 },
    { key: 'annual_interest_rate', value: String(inputs.annualInterestRate), sort_order: 4 },
    { key: 'management_fee_rate', value: String(inputs.managementFeeRate), sort_order: 5 },
  ];
}

// --- Helpers ---

function getEmptyOutputs(rate: number = 0): PricingOutputs {
  return {
    contractWeeks: 0,
    contractYears: 0,
    driverPrice: 0,
    poolWeekly: 0,
    poolTotal: 0,
    partnerWeekly: 0,
    managementWeekly: 0,
    insuranceWeekly: 0,
    partnerGrossProfit: 0,
    partnerNetProfit: 0,
    partnerTotalReturn: 0,
    partnerRoiPct: 0,
    partnerAnnualizedRoi: 0,
    partnerBreakevenWeeks: 0,
    insuranceTotal: 0,
    insuranceInterest: 0,
    insuranceBreakevenWeeks: 0,
    insuranceRoiPct: 0,
    primelaneInsuranceProfit: 0,
    primelaneManagementProfit: 0,
    spillPerWeek: 0,
    spillToPartner: 0,
    spillToManagement: 0,
    annualInterestRate: rate,
  };
}
